/**
 * Audio policy (doc 18 §1) — every rule about WHETHER and HOW LOUD, with no
 * WebAudio in sight.
 *
 * It lives apart from the engine because these are the rules that go wrong: a
 * 30-krill wave firing thirty identical hit sounds is an audio wall, and you
 * cannot unit-test a wall through an AudioContext. Everything here is pure and
 * takes its clock as an argument.
 */

export type Bus = 'music' | 'sfx' | 'ambience';
/** Doc 18 §1: gameplay-critical beats feedback beats ambient, when it is full. */
export type Priority = 'critical' | 'feedback' | 'ambient';

export const PRIORITY_RANK: Record<Priority, number> = { critical: 2, feedback: 1, ambient: 0 };

/** Doc 18 §1, verbatim: four at once, and 40 ms between retriggers. */
export const MAX_PER_SOUND = 4;
export const RETRIGGER_MS = 40;
/** Total live one-shots. Past this, priority decides who is speaking. */
export const MAX_VOICES = 24;
/** Doc 18 §1: ambience drops 6 dB under a banner. */
export const DUCK_DB = -6;
/** Doc 17 §4: off-screen action is audible without being disorienting. */
export const MAX_PAN = 0.4;

export interface LiveVoice {
  id: string;
  priority: Priority;
  startedAt: number;
  endsAt: number;
}

/**
 * Who is allowed to make a noise right now. `admit` is the whole contract:
 * it returns null to refuse, or the voice to start — and it evicts on its own
 * when something more important than what is playing arrives.
 */
export class VoiceBudget {
  private live: LiveVoice[] = [];
  /** Voices evicted by `admit`, drained by the engine so it can stop them. */
  readonly evicted: LiveVoice[] = [];

  /** Retire anything whose end has passed. Called before every decision. */
  reap(now: number): void {
    this.live = this.live.filter((v) => v.endsAt > now);
  }

  get count(): number {
    return this.live.length;
  }

  countOf(id: string): number {
    return this.live.filter((v) => v.id === id).length;
  }

  admit(id: string, priority: Priority, now: number, durationMs: number): LiveVoice | null {
    this.reap(now);
    const mine = this.live.filter((v) => v.id === id);
    if (mine.length >= MAX_PER_SOUND) return null;
    // the retrigger guard is per sound, not global: two different sounds in the
    // same millisecond is a busy fight, thirty of one is a bug
    const last = mine.reduce((t, v) => Math.max(t, v.startedAt), -Infinity);
    if (now - last < RETRIGGER_MS) return null;

    if (this.live.length >= MAX_VOICES) {
      const victim = this.weakest();
      if (!victim || PRIORITY_RANK[victim.priority] >= PRIORITY_RANK[priority]) return null;
      this.live.splice(this.live.indexOf(victim), 1);
      this.evicted.push(victim);
    }
    const voice: LiveVoice = { id, priority, startedAt: now, endsAt: now + durationMs };
    this.live.push(voice);
    return voice;
  }

  /** Lowest priority, and among equals the one that has been going longest. */
  private weakest(): LiveVoice | undefined {
    return this.live.reduce<LiveVoice | undefined>((worst, v) => {
      if (!worst) return v;
      const a = PRIORITY_RANK[v.priority];
      const b = PRIORITY_RANK[worst.priority];
      if (a < b) return v;
      if (a === b && v.startedAt < worst.startedAt) return v;
      return worst;
    }, undefined);
  }

  clear(): void {
    this.live = [];
    this.evicted.length = 0;
  }
}

/** Slider 0..100 → linear gain, with a perceptual curve rather than a ramp. */
export function sliderGain(value: number): number {
  const v = Math.max(0, Math.min(100, value)) / 100;
  return v * v; // equal-ish loudness steps; 50 sounds like half, not like 71%
}

export function dbToGain(db: number): number {
  return 10 ** (db / 20);
}

/**
 * Doc 17 §4: pan by screen-x, clamped so nothing is ever hard-panned. `x` and
 * `width` are screen pixels; anything off-screen sits at the clamp.
 */
export function panFor(x: number, width: number): number {
  if (width <= 0) return 0;
  const t = (x / width) * 2 - 1;
  return Math.max(-MAX_PAN, Math.min(MAX_PAN, t));
}
